<script lang="ts">
	/**
	 * Signing in **without leaving the store**, and without anybody having to register first
	 * (Design §14).
	 *
	 * The customer gives an email address, AbstractID emails a six-digit code, and verifying it
	 * hands back the same JWT the `/authorize` redirect would have. AbstractID **creates the
	 * account itself** on that first request, so "buy without an account" needs no account: the
	 * purchase and the identity are established by the same six digits.
	 *
	 * Three properties hold this together, and each is load-bearing:
	 *
	 *   - **It never redirects.** Not on any branch. This runs inside the purchase confirmation
	 *     window, where a redirect would abandon a window the customer opened, discard the seat
	 *     count and coupon they chose in it, and return them — if at all — to a page that has to
	 *     reconstruct all of it. That is the reason the flow is local at all, so an "unless…" here
	 *     would defeat the whole thing. The redirect to the identity provider still exists, in
	 *     `AbstractID.beginSignIn`, and is what **every regular sign-in** uses; it is simply not
	 *     part of buying.
	 *   - **The code is always forced.** `requestEmailOTP(address, true)` bypasses any saved
	 *     `password` / `passkey` preference on the account, which without the flag comes back as a
	 *     method name and no code. Honouring that preference would mean handing off — the one
	 *     thing this must not do — so the reply would name a route with nowhere to go and the
	 *     customer would be stuck on a screen waiting for an email nobody sent.
	 *   - **The account-existence question is never answered.** The reply for an address nobody has
	 *     seen is byte for byte the reply for one that has bought forty times, because the account
	 *     is created when the code is requested. Nothing here may branch on "new customer".
	 *
	 * TOTP is carried inline too, because it is a second question about a code rather than a second
	 * credential: a correct OTP from an account with TOTP enabled comes back `requiresTwoFactor`,
	 * which is a step forward and must not read as a wrong code.
	 *
	 * It renders **inline and owns no window of its own** — the caller supplies the surface. The
	 * one caller is `CheckoutSteps`, which renders it as a step on the page; a modal of ours inside
	 * would stack two dialogs over one decision.
	 *
	 * `signedin` fires only once the token is stored and the refresh loop is running, so a parent
	 * may act on it immediately — the confirmation window carries straight on with the purchase.
	 */

	import { createEventDispatcher, tick } from 'svelte';
	import { Button, Input, Spinner } from 'flowbite-svelte';
	import FormField from './FormField.svelte';
	import OTPInput from './OTPInput.svelte';
	import AbstractID from '../AbstractID';
	import { track } from '../stores/analytics';
	import { isValidEmail } from '../Utilities';

	/** Headline above the form. Empty renders none, for a caller that supplies its own. */
	export let heading: string = '';
	/** The line under it. Say what happens next, not what the control is. */
	export let subheading: string = '';
	/** Prefills the address, for a customer whose email is already known. */
	export let email: string = '';
	/**
	 * Blocks the flow while the caller is not ready for it — unaccepted terms, above all.
	 *
	 * Signing in *is* the confirmation here: the window carries straight on to payment once the
	 * code lands. So a screen whose terms are unticked must not be able to start the code, or the
	 * tick would be enforced only by the server, on a purchase the customer believes they made.
	 */
	export let disabled: boolean = false;

	const dispatcher = createEventDispatcher<{ signedin: void }>();

	/**
	 * `email` asks for the address, `otp` for the mailed code, `two-factor` for the authenticator's.
	 * There is no `password` step by design — see the header.
	 */
	let mode: 'email' | 'otp' | 'two-factor' = 'email';

	let loading: boolean = false;
	/** Shown above the form: a refusal, in the identity provider's own words. */
	let errorMessage: string = '';
	/** Shown under the email field: our own answer, before anything was sent. */
	let emailError: string = '';
	/** Confirmation that a fresh code is on its way, since resending is otherwise silent. */
	let notice: string = '';

	/**
	 * The address the code was actually sent to, frozen at the moment of sending.
	 *
	 * Separate from `email`, which stays bound to the field: verifying against a value the customer
	 * could still edit would check the code against an address it was never sent to, and report a
	 * correct code as wrong.
	 */
	let sentTo: string = '';
	/** The short-lived token a TOTP account is given in place of a session. */
	let twoFactorToken: string = '';

	let otpInput: OTPInput;

	/*
	 * A prefilled address never requests a code on its own. A code sent by a window merely being
	 * opened is a code nobody asked for, and on a link somebody shared it lands in a stranger's
	 * inbox — so this always opens on `email`, prefilled or not.
	 */

	function fnClearMessages(): void {
		errorMessage = '';
		emailError = '';
		notice = '';
	}

	/**
	 * Asks AbstractID to email the code.
	 *
	 * Always `force`. See the header: honouring a saved password or passkey preference would mean
	 * handing the customer to the identity provider, which this must never do, so the reply would
	 * name a method with nowhere to go.
	 */
	async function fnRequestCode(resend: boolean = false): Promise<void> {
		if (disabled) return;

		const address = email.trim();

		fnClearMessages();

		if (address.length === 0) {
			emailError = 'Please enter your email address.';
			return;
		}
		if (!isValidEmail(address)) {
			emailError = 'That does not look like an email address.';
			return;
		}

		loading = true;

		try {
			await AbstractID.requestEmailOTP(address, true);

			/*
			 * §12.2's `engaged` stage, and the point in a funnel where a visitor stops browsing and
			 * commits something of their own. **Never the address**: it identifies a person, it is
			 * held on the account already, and an analytics table is not where a customer would ever
			 * expect to find it. `resend` is here because a second request usually means the first
			 * email did not arrive, which is a deliverability problem this is the only record of.
			 */
			track('signin_started', { properties: { resend } });

			sentTo = address;
			mode = 'otp';
			notice = resend ? 'A new code is on its way.' : '';
			loading = false;

			// The boxes are only in the DOM once the step change has rendered, and focusing a
			// disabled input does nothing — so `loading` drops first. Same ordering as below.
			await tick();
			otpInput?.focus();
		} catch (error) {
			errorMessage = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	/** Stores the token and tells the parent, which is what actually completes the sign-in. */
	async function fnAdopt(token: string): Promise<void> {
		await AbstractID.completeSignIn(token);
		dispatcher('signedin');
	}

	/**
	 * Reports a refused code and hands the caret back to an empty first box.
	 *
	 * The clearing happens **after** `loading` drops, and that ordering is the whole reason this is
	 * a function rather than two lines: the boxes are `disabled` while a code is in flight, and
	 * focusing a disabled input does nothing at all — so a customer whose code was refused would be
	 * looking at six empty boxes with the caret nowhere, having to click back into them.
	 */
	async function fnRefuseCode(error: unknown): Promise<void> {
		errorMessage = (error as Error).message;
		loading = false;

		await tick();
		otpInput?.reset();
	}

	async function fnVerifyCode(event: CustomEvent<string>): Promise<void> {
		fnClearMessages();
		loading = true;

		try {
			const result = await AbstractID.verifyEmailOTP(sentTo, event.detail);

			/*
			 * Not a failure: the code was right and the account carries a second factor.
			 *
			 * `loading` drops before the focus call for the reason `fnRefuseCode` documents. The
			 * control itself is replaced rather than reused, by the `{#key mode}` around it: the
			 * six boxes still hold the emailed code, and reusing them would fire `complete` on the
			 * very first keystroke with five of the old digits still in place.
			 */
			if (result.requiresTwoFactor === true && typeof result.twoFactorToken === 'string') {
				twoFactorToken = result.twoFactorToken;
				mode = 'two-factor';
				loading = false;

				await tick();
				otpInput?.focus();
				return;
			}

			if (typeof result.token !== 'string' || result.token.length === 0) {
				throw new Error('The identity provider did not return a sign-in token.');
			}

			await fnAdopt(result.token);
		} catch (error) {
			await fnRefuseCode(error);
		} finally {
			loading = false;
		}
	}

	async function fnVerifyTwoFactor(event: CustomEvent<string>): Promise<void> {
		fnClearMessages();
		loading = true;

		try {
			const result = await AbstractID.verifyTwoFactor(twoFactorToken, event.detail);

			if (typeof result.token !== 'string' || result.token.length === 0) {
				throw new Error('The identity provider did not return a sign-in token.');
			}

			await fnAdopt(result.token);
		} catch (error) {
			await fnRefuseCode(error);
		} finally {
			loading = false;
		}
	}

	/** Back to the address, which is where somebody who mistyped it needs to be. */
	function fnBackToEmail(): void {
		fnClearMessages();
		twoFactorToken = '';
		mode = 'email';
	}
</script>

<div class="space-y-3">
	{#if heading.length > 0}
		<div>
			<h3 class="font-semibold">{heading}</h3>
			{#if subheading.length > 0}
				<p class="text-sm text-default mt-1">{subheading}</p>
			{/if}
		</div>
	{/if}

	{#if errorMessage.length > 0}
		<!-- The provider's own words: "Invalid or expired OTP." is more use than a sentence of ours. -->
		<p class="text-sm text-red-600 dark:text-red-400" role="alert">
			<i class="fa-solid fa-circle-exclamation mr-1" />{errorMessage}
		</p>
	{/if}

	{#if mode === 'email'}
		<!--
			Not a `<form>`. This renders inside `CheckoutSteps`, which the product
			page also mounts inside its own markup; a nested form is invalid HTML, and the browser
			resolves it by dropping the inner one — so Enter would submit the outer form instead.
			Enter is handled on the field directly, which is the behaviour a form would have given.
		-->
		<FormField label="Email" id="signin-email" required error={emailError}>
			<!--
				`type="text"` with `inputmode="email"`, deliberately, and for the same reason
				`CountrySelect` takes `aria-required` rather than the native attribute: a
				`type="email"` input has the browser refuse the submit itself, with a bubble reading
				"Please enter an email address" — a message about a text box, in place of the
				screen's own sentence, and with this component's validation never running at all.
			-->
			<Input
				id="signin-email"
				type="text"
				inputmode="email"
				autocomplete="email"
				placeholder="you@example.com"
				bind:value={email}
				disabled={loading || disabled}
				on:keydown={event => {
					if (event.key === 'Enter') {
						event.preventDefault();
						void fnRequestCode();
					}
				}}
			/>
		</FormField>

		<Button color="primary" class="w-full" disabled={loading || disabled} on:click={() => fnRequestCode()}>
			{#if loading}<Spinner size="5" class="mr-2" />{/if}
			<i class="fa-solid fa-paper-plane mr-2" />Email me a sign-in code
		</Button>

		<p class="text-xs text-default text-center">
			No account needed. We will create one for you, and this is also how you sign in next time.
		</p>
	{:else}
		<p class="text-sm text-default">
			{#if mode === 'two-factor'}
				Enter the 6-digit code from your authenticator app.
			{:else}
				We sent a 6-digit code to
				<strong class="text-gray-700 dark:text-gray-300">{sentTo}</strong>. Enter it below.
			{/if}
		</p>

		<!--
			Keyed on the mode, so the emailed code and the authenticator's are typed into two
			different controls rather than one reused. Without it the boxes still hold six digits
			when the second question is asked, and the first keystroke completes them again — a code
			the customer never typed, refused, and blamed on themselves.
		-->
		{#key mode}
			<OTPInput
				bind:this={otpInput}
				disabled={loading}
				error={errorMessage.length > 0 ? ' ' : ''}
				on:complete={mode === 'two-factor' ? fnVerifyTwoFactor : fnVerifyCode}
			/>
		{/key}

		{#if notice.length > 0}
			<p class="text-sm text-green-600 dark:text-green-400 text-center" role="status">{notice}</p>
		{/if}

		<div class="flex flex-wrap justify-center items-center gap-4">
			{#if mode === 'otp'}
				<button
					type="button"
					class="text-sm text-primary-600 hover:underline dark:text-primary-400"
					disabled={loading}
					on:click={() => fnRequestCode(true)}
				>
					<i class="fa-solid fa-rotate-right mr-1" />Resend code
				</button>
				<span class="text-gray-300 dark:text-gray-600">|</span>
			{/if}
			<button
				type="button"
				class="text-sm text-default hover:text-primary-600 dark:hover:text-primary-400"
				disabled={loading}
				on:click={fnBackToEmail}
			>
				<i class="fa-solid fa-arrow-left mr-1" />
				{mode === 'two-factor' ? 'Start again' : 'Use a different email'}
			</button>
		</div>
	{/if}
</div>
